// PE Delivery Loop — path-agnostic locked-sequence engine (v1).
//
// ONE headless engine. No DOM, no route assumptions, no LTI/native branching.
// Both the native (`/student/lesson/:assignmentId`) and LTI (`/lti/scenes`)
// surfaces mount THIS module in later phases — parity is structural, not copied.
//
// The engine owns three things and nothing else:
//   1. The canonical sequence state machine (§3), order-locked.
//   2. The 7-asset student split (§2): teacher-only types are NOT representable
//      in a student payload — rejected at the type boundary, not hidden.
//   3. The completion contract (§6): it enforces ORDER on per-step completion
//      signals; it does not know HOW a surface decides a step is complete.
//
// Input  = a resolved (PK-resolved) chapter payload.
// Output = current step, allowed transitions, completion state.
//
// Tier/lang come from the composite assignment key `pe:{PK}:{tier}:{lang}`,
// never from pe_chapters (no such columns — confirmed in Phase-0 recon).

// Mirrors src/api/serving/types.ts (kept inline so the engine has ZERO runtime
// imports and stays a pure, independently-testable module).
export type Tier = "T1" | "T2" | "T3" | "T4";
export type Lang = "en" | "es";

// ---------------------------------------------------------------------------
// §2 — Asset classification (13 assets; exactly 7 deployable to students)
// ---------------------------------------------------------------------------

/** The 7 — and only 7 — asset types that may appear in a STUDENT payload. */
export type StudentAssetType =
  | "episode_video" // 1  Full Animated Episode
  | "scene_reader" // 2  Scene Reader
  | "chapter_activity" // 3  Chapter Activity (execution half is student-facing)
  | "vocab_apply" // 6  Vocabulary Word Cards (Apply half is student-facing)
  | "formatives" // 9  Embedded Formative Items
  | "summative_quiz" // 10 Summative Quiz (grades 3–4 only)
  | "activity_booklet"; // 11 Student Activity Booklet

/** The 6 teacher-only asset types. NEVER valid in a student payload. */
export type TeacherOnlyAssetType =
  | "teacher_guide" // 4
  | "quick_reference" // 5
  | "read_aloud_script" // 7  (K–1)
  | "observation_checklist" // 8  (K–1)
  | "standards_alignment" // 13
  | "vocab_launch"; // 6  Vocabulary Word Cards (Launch half is teacher-only)

/** Caregiver plane — neither student-deployable nor part of the teacher hub. */
export type CaregiverAssetType = "family_letter"; // 12

export type AssetType =
  | StudentAssetType
  | TeacherOnlyAssetType
  | CaregiverAssetType;

export const STUDENT_ASSET_TYPES: readonly StudentAssetType[] = [
  "episode_video",
  "scene_reader",
  "chapter_activity",
  "vocab_apply",
  "formatives",
  "summative_quiz",
  "activity_booklet",
] as const;

export const TEACHER_ONLY_ASSET_TYPES: readonly TeacherOnlyAssetType[] = [
  "teacher_guide",
  "quick_reference",
  "read_aloud_script",
  "observation_checklist",
  "standards_alignment",
  "vocab_launch",
] as const;

const STUDENT_ASSET_SET = new Set<string>(STUDENT_ASSET_TYPES);
const TEACHER_ONLY_ASSET_SET = new Set<string>(TEACHER_ONLY_ASSET_TYPES);

export function isStudentAssetType(t: string): t is StudentAssetType {
  return STUDENT_ASSET_SET.has(t);
}

export function isTeacherOnlyAssetType(t: string): t is TeacherOnlyAssetType {
  return TEACHER_ONLY_ASSET_SET.has(t);
}

// ---------------------------------------------------------------------------
// §3 — Canonical sequence (sequence-locked)
// ---------------------------------------------------------------------------

export type StepId =
  | "vocab" // 1 Vocabulary (Apply cards)        — Pre-training
  | "video" // 2 Video                            — Modeling
  | "reader" // 3 Scene Reader (word-by-word narration) — Guided
  | "formative" // 4 Embedded Formative Items (image-MC/drag + oral) — Guided practice
  | "activity" // 5 Activity Booklet / Chapter Activity — Independent
  | "summative"; // 6 Summative Quiz (grades 3–4 only)   — You Do

interface StepDef {
  id: StepId;
  /** A step is active iff the payload includes ≥1 of these student assets. */
  assets: readonly StudentAssetType[];
  /** When set, the step only exists for these tiers (summative = T3/T4 only). */
  tiersOnly?: readonly Tier[];
}

/** The canonical order. Index = lock order; you cannot reorder this. */
const CANONICAL_STEPS: readonly StepDef[] = [
  { id: "vocab", assets: ["vocab_apply"] },
  { id: "video", assets: ["episode_video"] },
  { id: "reader", assets: ["scene_reader"] },
  // Formatives are their own order-locked step (all tiers), at parity with the
  // /lti/assessment surface: the embedded image-MC / tap-to-place / oral items
  // render via FormativeAssessment(source="assessment"). (Previously folded into
  // the reader step's assets and never delivered interactively in-product.)
  { id: "formative", assets: ["formatives"] },
  { id: "activity", assets: ["activity_booklet", "chapter_activity"] },
  { id: "summative", assets: ["summative_quiz"], tiersOnly: ["T3", "T4"] },
] as const;

/**
 * Canonical student-asset emit order — the flattened asset order implied by
 * CANONICAL_STEPS. A deployment payload's `assets` MUST follow this order so the
 * engine derives the same locked step list regardless of how the picker's UI
 * groups its toggles. Single source of truth for the Phase-4 DeploymentPicker
 * emit ordering; the picker imports this rather than hardcoding an order.
 */
export const CANONICAL_ASSET_ORDER: readonly StudentAssetType[] =
  CANONICAL_STEPS.flatMap((s) => s.assets);

/**
 * Narration: word-by-word synced narration defaults ON and is LOCKED for all
 * v1 tiers. There is no audio-OFF toggle in v1 — this constant is the whole
 * contract; the engine exposes no setter.
 */
export const NARRATION_LOCKED_ON = true as const;

// ---------------------------------------------------------------------------
// Errors — every illegal operation has a typed, catchable error.
// ---------------------------------------------------------------------------

/** A teacher-only (or otherwise non-student) asset reached the type boundary. */
export class TeacherAssetRejectedError extends Error {
  constructor(public readonly asset: string) {
    super(
      `Asset "${asset}" is not student-deployable and cannot appear in a student payload.`,
    );
    this.name = "TeacherAssetRejectedError";
  }
}

/** A completion signal arrived for a step that is not the current step. */
export class OutOfOrderError extends Error {
  constructor(
    public readonly attempted: StepId,
    public readonly current: StepId | null,
  ) {
    super(
      `Cannot complete step "${attempted}" — the current step is ${
        current ? `"${current}"` : "none (sequence already complete)"
      }. Steps are order-locked.`,
    );
    this.name = "OutOfOrderError";
  }
}

/** The resolved payload produced no active steps (e.g. picker emitted nothing). */
export class EmptySequenceError extends Error {
  constructor() {
    super("Resolved payload contains no student-deployable steps.");
    this.name = "EmptySequenceError";
  }
}

// ---------------------------------------------------------------------------
// Input + state
// ---------------------------------------------------------------------------

/** A resolved, PK-resolved chapter payload — the engine's only input. */
export interface ResolvedChapterPayload {
  /** pe_chapters PK (already resolved; e.g. S1 Ch1 = "2"). NOT the chapter number. */
  chapterId: string;
  tier: Tier;
  language: Lang;
  /**
   * The student-facing assets included in THIS assignment. The Phase-4 picker
   * supplies a subset (à-la-carte); a full-chapter assignment supplies all
   * applicable student assets. Only StudentAssetType values are legal here.
   */
  assets: StudentAssetType[];
}

/** Immutable engine state. Transitions return a new SequenceState. */
export interface SequenceState {
  readonly chapterId: string;
  readonly tier: Tier;
  readonly language: Lang;
  /** Active steps in canonical order, gated by payload assets + tier. */
  readonly steps: readonly StepId[];
  /** Per-step completion flags. */
  readonly completed: Readonly<Record<StepId, boolean>>;
}

/** A flattened, surface-friendly view of the current state. */
export interface SequenceView {
  chapterId: string;
  tier: Tier;
  language: Lang;
  steps: StepId[];
  completed: Record<StepId, boolean>;
  /** First incomplete step, or null when the whole sequence is complete. */
  currentStep: StepId | null;
  /** Steps the student may currently be on: completed (revisit) + current. */
  allowedTransitions: StepId[];
  isComplete: boolean;
  /** Always true in v1 — narration is locked ON. */
  narrationLocked: boolean;
}

// ---------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------

/**
 * Build a locked sequence from a resolved payload.
 *
 * Rejects teacher-only (and any non-student) asset types AT THE TYPE BOUNDARY
 * (structural half of the picker hard-block). Derives the active, order-locked
 * step list from the assets present, honoring the tier gate (summative = T3/T4).
 */
export function createSequence(
  payload: ResolvedChapterPayload,
): SequenceState {
  // 1. Type-boundary rejection — a teacher-only asset is NOT representable here.
  for (const asset of payload.assets) {
    if (!isStudentAssetType(asset)) {
      throw new TeacherAssetRejectedError(asset);
    }
  }

  const present = new Set<StudentAssetType>(payload.assets);

  // 2. Derive active steps in canonical order.
  const steps: StepId[] = [];
  for (const def of CANONICAL_STEPS) {
    if (def.tiersOnly && !def.tiersOnly.includes(payload.tier)) continue;
    if (def.assets.some((a) => present.has(a))) steps.push(def.id);
  }

  if (steps.length === 0) throw new EmptySequenceError();

  const completed = {} as Record<StepId, boolean>;
  for (const id of steps) completed[id] = false;

  return Object.freeze({
    chapterId: payload.chapterId,
    tier: payload.tier,
    language: payload.language,
    steps: Object.freeze([...steps]),
    completed: Object.freeze(completed),
  });
}

// ---------------------------------------------------------------------------
// Queries (pure)
// ---------------------------------------------------------------------------

/** First incomplete step in canonical order, or null when all are complete. */
export function currentStep(state: SequenceState): StepId | null {
  for (const id of state.steps) {
    if (!state.completed[id]) return id;
  }
  return null;
}

export function isComplete(state: SequenceState): boolean {
  return currentStep(state) === null;
}

/**
 * Steps the student is allowed to be on right now: every completed step (so
 * they may revisit) plus the single current step. A future, still-locked step
 * is never included — this is the gate.
 */
export function allowedTransitions(state: SequenceState): StepId[] {
  const cur = currentStep(state);
  const allowed = state.steps.filter((id) => state.completed[id]);
  if (cur) allowed.push(cur);
  return allowed;
}

export function canAdvanceTo(state: SequenceState, step: StepId): boolean {
  return allowedTransitions(state).includes(step);
}

// ---------------------------------------------------------------------------
// Transition (pure — returns a new state)
// ---------------------------------------------------------------------------

/**
 * Record a completion signal for `step`. The engine enforces order: only the
 * CURRENT step may be completed. Completing a future step (jump-ahead) or any
 * non-current step throws OutOfOrderError — surfaces report the current step
 * only, and the gate opens exactly one step at a time.
 */
export function completeStep(
  state: SequenceState,
  step: StepId,
): SequenceState {
  const cur = currentStep(state);
  if (step !== cur) throw new OutOfOrderError(step, cur);

  const completed = { ...state.completed, [step]: true };
  return Object.freeze({
    ...state,
    completed: Object.freeze(completed),
  });
}

/** Flatten state into a surface-friendly view. */
export function view(state: SequenceState): SequenceView {
  return {
    chapterId: state.chapterId,
    tier: state.tier,
    language: state.language,
    steps: [...state.steps],
    completed: { ...state.completed },
    currentStep: currentStep(state),
    allowedTransitions: allowedTransitions(state),
    isComplete: isComplete(state),
    narrationLocked: NARRATION_LOCKED_ON,
  };
}
